/**
 * Everything the diagram needs, straight from the data the report carries.
 *
 * The prototype shipped a parser for Polish algebraic notation because its
 * mock data only had "13.Sg3". The real dataset carries the UCI of both moves
 * beside the SAN, so nothing here has to work out what a move means: squares
 * come from the FEN, arrows come from four characters of UCI. That deletes the
 * one part of the prototype that could have drawn a wrong arrow.
 */

export const FILES = 'abcdefgh'

/** The prototype's board, kept to the pixel: lichess brown over warm paper. */
export const LIGHT_SQUARE = '#F0D9B5'
export const DARK_SQUARE = '#B58863'
export const PLAYED_TINT = 'rgba(31, 122, 70, 0.34)'
export const ALTERNATIVE_TINT = 'rgba(192, 132, 25, 0.34)'

export type PieceColour = 'w' | 'b'

export type Square = {
  name: string
  /** Piece code as the cburnett files are named: "wK", "bP", … */
  piece: string | null
  light: boolean
  /** Which move touched this square, if any. */
  mark: 'played' | 'alternative' | null
  /** Coordinates are printed on the edge squares only, as on the diagram. */
  fileLabel: string
  rankLabel: string
}

export type ParsedFen = {
  pieces: Map<string, string>
  turn: PieceColour
}

export function parseFen(fen: string): ParsedFen {
  const [placement, turn] = fen.trim().split(/\s+/)
  const pieces = new Map<string, string>()
  placement?.split('/').forEach((row, index) => {
    const rank = 8 - index
    let file = 0
    for (const character of row) {
      if (/\d/.test(character)) {
        file += Number(character)
      } else {
        pieces.set(`${FILES[file]}${rank}`, character)
        file += 1
      }
    }
  })
  return { pieces, turn: turn === 'b' ? 'b' : 'w' }
}

/** "P" -> "wP", "p" -> "bP": the file name of the piece image. */
export function pieceCode(piece: string): string {
  return `${piece === piece.toUpperCase() ? 'w' : 'b'}${piece.toUpperCase()}`
}

export type Marks = Record<string, 'played' | 'alternative'>

/**
 * Eight rows of eight squares, oriented so the side to move is at the bottom.
 *
 * The report prints black-to-move diagrams flipped, and a reviewer comparing
 * screen with paper should not have to rotate one of them in their head.
 */
export function boardRows(fen: string, flipped: boolean, marks: Marks = {}): Square[][] {
  const { pieces } = parseFen(fen)
  const rows: Square[][] = []
  for (let i = 0; i < 8; i += 1) {
    const rank = flipped ? i + 1 : 8 - i
    const row: Square[] = []
    for (let j = 0; j < 8; j += 1) {
      const fileIndex = flipped ? 7 - j : j
      const name = `${FILES[fileIndex]}${rank}`
      const piece = pieces.get(name)
      row.push({
        name,
        piece: piece ? pieceCode(piece) : null,
        light: (fileIndex + rank - 1) % 2 === 1,
        mark: marks[name] ?? null,
        // Ranks down the right-hand edge, files along the bottom.
        rankLabel: j === 7 ? String(rank) : '',
        fileLabel: i === 7 ? (FILES[fileIndex] ?? '') : '',
      })
    }
    rows.push(row)
  }
  return rows
}

export type Arrow = {
  x1: number
  y1: number
  x2: number
  y2: number
  /** Three points of the head, ready for an SVG polygon. */
  head: string
}

const SIZE = 100

function centre(square: string, flipped: boolean): { x: number; y: number } {
  const file = FILES.indexOf(square[0] ?? 'a')
  const rank = Number(square[1]) - 1
  return {
    x: (flipped ? 7 - file : file) * SIZE + SIZE / 2,
    y: (flipped ? rank : 7 - rank) * SIZE + SIZE / 2,
  }
}

/**
 * Arrow between the two squares of a UCI move, in a 800x800 viewBox.
 *
 * Geometry taken from the prototype: the shaft starts clear of the piece it
 * leaves and stops where the head begins, so a short move still reads as an
 * arrow rather than as a blob.
 */
export function arrow(uci: string, flipped: boolean): Arrow | null {
  if (uci.length < 4) return null
  const from = centre(uci.slice(0, 2), flipped)
  const to = centre(uci.slice(2, 4), flipped)
  const dx = to.x - from.x
  const dy = to.y - from.y
  const length = Math.hypot(dx, dy) || 1
  const ux = dx / length
  const uy = dy / length
  const px = -uy
  const py = ux
  const start = { x: from.x + ux * 26, y: from.y + uy * 26 }
  const tip = { x: to.x - ux * 6, y: to.y - uy * 6 }
  const base = { x: tip.x - ux * 46, y: tip.y - uy * 46 }
  const wing1 = { x: base.x + px * 27, y: base.y + py * 27 }
  const wing2 = { x: base.x - px * 27, y: base.y - py * 27 }
  return {
    x1: start.x,
    y1: start.y,
    x2: base.x + ux * 12,
    y2: base.y + uy * 12,
    head: `${tip.x},${tip.y} ${wing1.x},${wing1.y} ${wing2.x},${wing2.y}`,
  }
}

export function squaresOf(uci: string): [string, string] {
  return [uci.slice(0, 2), uci.slice(2, 4)]
}
